import { Link, type LinkProps } from 'expo-router';
import { Children, ReactElement, ReactNode, cloneElement } from 'react';
import { Pressable } from 'react-native';

import type { ActionHandler, PageBuilderLinkAction } from '../../types';

import * as constants from './constants';

function PotentiallyExternalLink({
  uri,
  children,
  onPress,
  asChild,
}: {
  uri: string;
  children: ReactNode;
  onPress: LinkProps['onPress'];
  asChild?: boolean;
}) {
  const isExternal = /^https?:\/\//.test(uri);

  return (
    <Link
      href={uri}
      target={isExternal ? '_blank' : '_self'}
      rel={isExternal ? 'noopener noreferrer' : ''}
      onPress={onPress}
      asChild={asChild}
    >
      {children}
    </Link>
  );
}

export type ActionLinkProps = {
  action: PageBuilderLinkAction;
  onAction?: ActionHandler;
  children: ReactNode;
  asChild?: boolean;
};

export function ActionLink({ action, onAction, children, asChild }: ActionLinkProps) {
  switch (action.action) {
    case 'open-href': {
      const uri = action.href!;

      return (
        <PotentiallyExternalLink uri={uri} onPress={() => onAction?.(action)} asChild={asChild}>
          {children}
        </PotentiallyExternalLink>
      );
    }

    case 'open-do-not-sell-or-share-personal-information-modal': {
      if (asChild) {
        return children;
      }

      return <Pressable onPress={() => onAction?.(action)}>{children}</Pressable>;
    }

    case 'start-order-flow': {
      switch (action.startOrderFlowServiceMode) {
        case 'pickup': {
          return (
            <PotentiallyExternalLink
              uri="/store-locator/service-mode"
              onPress={() => onAction?.(action)}
              asChild={asChild}
            >
              {children}
            </PotentiallyExternalLink>
          );
        }

        case 'delivery': {
          return (
            <PotentiallyExternalLink
              uri="/store-locator/address"
              onPress={() => onAction?.(action)}
              asChild={asChild}
            >
              {children}
            </PotentiallyExternalLink>
          );
        }

        case 'catering': {
          return (
            <PotentiallyExternalLink
              uri={constants.CATERING_HREF}
              onPress={() => onAction?.(action)}
              asChild={asChild}
            >
              {children}
            </PotentiallyExternalLink>
          );
        }

        case 'ask': {
          if (asChild) {
            return cloneElement(Children.only(children) as ReactElement, {
              onPress: () => onAction?.(action),
            });
          }

          return children;
        }

        default: {
          return children;
        }
      }
    }

    default: {
      return children;
    }
  }
}
